import { toast } from 'react-toastify';
import { configService } from '../config/config-service';
import { getMsalAuthHeader } from '../auth/msal';

class HttpInterceptor {
  constructor() {
    // Get base URL from config service instead of environment variable
    this.baseUrl = configService.apiUrl;
    this.timeout = configService.timeout;
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    // const token = localStorage.getItem('Token');
    // if (token) {
    //   headers['Authorization'] = `Bearer ${token}`;
    // }
    
    return {
      ...headers,
      ...this.getAuthHeaders()
    }
  }

  getAuthHeaders() {
    const headers ={};
    const token = localStorage.getItem('Token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  async handleResponse(response) {
    const clonedResponse = response.clone();

    if (!response.ok) {
      let errorMessage = '';
      try {
        const errorData = await clonedResponse.json();
        errorMessage = errorData.message || 'An error occurred';
        
        // Check if the error is due to token expiration
        if (response.status === 401) {
          console.log('Token expired or unauthorized access');
          this.handleTokenExpiration(errorData);
          throw new Error('Authentication token expired. Please log in again.');
        }
      } catch (error) {
        errorMessage = 'An unexpected error occurred';
      }

      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 3000,
      });

      throw new Error(errorMessage);
    }

    const contentType = response.headers.get('content-type');

    if (contentType?.includes('application/json')) {
      return response.json();
    }

    if (
      contentType?.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    ) {
      return response.blob();
    }

    return response.text();
  }

  /**
   * @param {string} endpoint
   * @param {Record<string, string>} [params]
   * @param {{ timeoutMs?: number }} [options] timeoutMs overrides config TIMEOUT for this request only
   */
  async get(endpoint, params = {}, options = {}) {
    const timeoutMs =
      typeof options.timeoutMs === 'number' && options.timeoutMs > 0
        ? options.timeoutMs
        : this.timeout;
    const queryString = new URLSearchParams(params).toString();
    const url = `${this.baseUrl}${endpoint}${queryString ? `?${queryString}` : ''}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const msalHeader = await getMsalAuthHeader();
      const response = await fetch(url, {
        method: 'GET',
        headers: { ...this.getHeaders(), ...msalHeader },
        signal: controller.signal,
      });

      return await this.handleResponse(response);
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out');
      }
      this.handleError(error);
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * @param {string} endpoint
   * @param {unknown} data
   * @param {{ timeoutMs?: number }} [options] timeoutMs overrides config TIMEOUT for this request only
   */
  async post(endpoint, data, options = {}) {
    const timeoutMs =
      typeof options.timeoutMs === 'number' && options.timeoutMs > 0
        ? options.timeoutMs
        : this.timeout;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      const msalHeader = await getMsalAuthHeader();
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: { ...this.getHeaders(), ...msalHeader },
        body: JSON.stringify(data),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return await this.handleResponse(response);
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out');
      }
      this.handleError(error);
      throw error;
    }
  }
  async postfile(endpoint, data) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        const msalHeader = await getMsalAuthHeader();
        const response = await fetch(`${this.baseUrl}${endpoint}`, {
        //const response = await fetch(`http://217.76.54.116:92/api/${endpoint}`, {
        method: 'POST',
        headers: { ...this.getAuthHeaders(), ...msalHeader },//this.getHeaders(),
        body: data,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return await this.handleResponse(response);
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out');
      }
      this.handleError(error);
      throw error;
    }
  }
  async put(endpoint, data) {
    try {
      const msalHeader = await getMsalAuthHeader();
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'PUT',
        headers: { ...this.getHeaders(), ...msalHeader },
        body: JSON.stringify(data),
      });

      return await this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async delete(endpoint) {
    try {
      const msalHeader = await getMsalAuthHeader();
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'DELETE',
        headers: { ...this.getHeaders(), ...msalHeader },
      });

      // Check status before calling handleResponse to preserve status for error handling
      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.clone().json();
        } catch (e) {
          // If JSON parsing fails, use text
          errorData = { message: await response.clone().text() };
        }
        
        // Return error object with status for proper error handling
        return {
          status: response.status,
          errorMessage: errorData,
          message: errorData?.message || 'Failed to delete'
        };
      }

      return await this.handleResponse(response);
    } catch (error) {
      // If handleResponse throws, wrap it with status info
      if (error.response) {
        return {
          status: error.response.status || 500,
          errorMessage: { message: error.message },
          message: error.message
        };
      }
      this.handleError(error);
      throw error;
    }
  }

  handleError(error) {
    const errorMessage = error.message || 'An unexpected error occurred';
    toast.error(errorMessage, {
      position: 'top-right',
      autoClose: 3000,
    });
  }

  // New method to handle token expiration
  handleTokenExpiration(errorData) {
    // Clear all auth data from localStorage
    localStorage.removeItem('LoginInfo');
    localStorage.removeItem('UserName');
    localStorage.removeItem('UserId');
    localStorage.removeItem('RoleId');
    localStorage.removeItem('Token');
    localStorage.removeItem('Company');
    
    // Show a message to the user
    toast.error('Your session has expired. Please log in again.', {
      position: 'top-right',
      autoClose: 5000,
      onClose: () => {
        // Redirect to login page after the toast closes
        window.location.href = '/signin';
      }
    });
  }
}

export const httpInterceptor = new HttpInterceptor();
